import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError } from '../utils/avatars';
import { getViolationRiderName } from '../utils/violations';
import './EventInviteModal.css';
import './EventTeamsSection.css';

// Состав команды на событие (Api::EventTeamsController#roster): кто из
// участников команды выступает за нее в этом событии. Выбрать можно только
// зарегистрированных на событие и не выступающих за другую команду; размер
// ограничен условиями события. mode: 'attach' — заявка команды с этим
// составом, 'edit' — правка состава уже заявленной команды
const EventTeamRosterModal = ({ event, team, mode, onClose, onSaved }) => {
  const { t, language } = useLanguage();
  const [members, setMembers] = useState([]);
  const [maxMembers, setMaxMembers] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const fetchRoster = async () => {
      try {
        const response = await axios.get(`/api/events/${event.id}/teams/${team.id}/roster`);
        if (cancelled) return;

        setMembers(response.data.members);
        setMaxMembers(response.data.max_team_members);
        // Отмечаем текущий состав; у еще не заявленной команды он пуст
        setSelected(new Set(response.data.members.filter(m => m.in_roster).map(m => m.id)));
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || t('eventTeams.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRoster();
    return () => { cancelled = true; };
  }, [event.id, team.id, t]);

  const limitReached = Boolean(maxMembers) && selected.size >= maxMembers;

  const toggle = (member) => {
    if (!member.selectable) return;

    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(member.id)) {
        next.delete(member.id);
      } else if (!maxMembers || next.size < maxMembers) {
        next.add(member.id);
      }
      return next;
    });
  };

  // Почему участника нельзя выбрать
  const reason = (member) => {
    if (member.other_team) return t('eventTeams.inOtherTeam').replace('{team}', member.other_team.name);
    if (!member.registered) return t('eventTeams.notRegistered');
    return null;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      const memberIds = Array.from(selected);
      if (mode === 'attach') {
        await axios.post(`/api/events/${event.id}/teams/${team.id}/attach`, { member_ids: memberIds });
      } else {
        await axios.put(`/api/events/${event.id}/teams/${team.id}/roster`, { member_ids: memberIds });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="event-invite-overlay" onClick={handleOverlayClick}>
      <div className="event-invite-modal">
        <div className="event-invite-header">
          <div>
            <h2>{t('eventTeams.rosterTitle')}</h2>
            <div className="event-invite-subtitle">{team.name} · {event.name}</div>
          </div>
          <button type="button" className="event-invite-close" onClick={onClose}>×</button>
        </div>

        <div className="event-invite-toolbar">
          <div className="event-invite-hint">{t('eventTeams.rosterHint')}</div>
        </div>

        <div className="event-invite-body">
          {loading ? (
            <div className="event-invite-message">{t('common.loading')}</div>
          ) : members.length === 0 ? (
            <div className="event-invite-message">{t('eventTeams.rosterEmpty')}</div>
          ) : (
            members.map(member => {
              const checked = selected.has(member.id);
              const blocked = !member.selectable || (!checked && limitReached);
              const note = reason(member);

              return (
                <label
                  key={member.id}
                  className={`event-invite-item ${!member.selectable ? 'event-invite-item-invited' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={blocked}
                    onChange={() => toggle(member)}
                  />
                  <img
                    src={getUserAvatarUrl(member.avatar)}
                    onError={handleUserAvatarError}
                    alt=""
                    className="event-invite-avatar"
                  />
                  <span className="event-invite-user">
                    <span className="event-invite-name">{getViolationRiderName(member, language)}</span>
                    <span className="event-invite-nickname">{note || `@${member.nickname}`}</span>
                  </span>
                  {member.role === 'captain' && (
                    <span className="event-team-captain">{t('eventTeams.captain')}</span>
                  )}
                </label>
              );
            })
          )}
        </div>

        <div className="event-invite-footer">
          <div className="event-invite-status">
            {error ? (
              <span className="event-invite-error">{error}</span>
            ) : (
              <span>
                {t('eventTeams.selectedCount').replace('{count}', selected.size)}
                {maxMembers ? ` / ${maxMembers}` : ''}
                {mode === 'attach' && selected.size === 0 && ` · ${t('eventTeams.rosterLater')}`}
              </span>
            )}
          </div>
          <button
            type="button"
            className="event-invite-submit"
            onClick={handleSubmit}
            disabled={submitting || loading}
          >
            {submitting
              ? t('eventTeams.saving')
              : mode === 'attach' ? t('eventTeams.submitAttach') : t('eventTeams.submitRoster')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventTeamRosterModal;
